import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import type { PendingConversation } from '../hooks/useOpenConversation';

interface ConversationModeSwitchDialogProps {
  pending: PendingConversation | null;
  onConfirm: () => void;
  onCancel: () => void;
  switching?: boolean;
}

/**
 * Confirms the mode change needed to view a conversation from the other branch.
 * Switching invalidates every query, so it's worth asking rather than doing it
 * behind the user's back.
 */
export function ConversationModeSwitchDialog({
  pending,
  onConfirm,
  onCancel,
  switching = false,
}: ConversationModeSwitchDialogProps) {
  const fromLabel = pending?.targetMode === 'develop' ? 'Production' : 'Develop';
  const toLabel = pending?.targetMode === 'develop' ? 'Develop' : 'Production';

  return (
    <AlertDialog open={!!pending} onOpenChange={(open) => { if (!open) onCancel(); }}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Switch to {toLabel} mode?</AlertDialogTitle>
          <AlertDialogDescription>
            This conversation happened in {toLabel} mode, but you're currently in {fromLabel} mode.
            Conversations are only visible in the mode they ran in, so opening it will switch you
            to {toLabel} mode and reload your data.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={switching}>Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={(e) => { e.preventDefault(); onConfirm(); }} disabled={switching}>
            {switching ? 'Switching…' : `Switch and open`}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
